import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    { 
      label: 'HOME', 
      path: '/', 
      icon: 'Home'
    },
    { 
      label: 'PRODUCT', 
      path: '/product', 
      icon: 'Package',
      hasDropdown: true,
      dropdownItems: [
        {
          title: 'Email Validator SaaS',
          description: 'Professional email validation service with 99.9% accuracy. Validate single emails or bulk lists to reduce bounce rates.',
          link: 'https://mailvalidator.octalchip.com/',
          icon: 'Mail'
        }
      ]
    },
    { 
      label: 'SERVICES', 
      path: '/services', 
      icon: 'Settings',
      hasDropdown: true,
      dropdownItems: [
        {
          title: 'Web Development',
          description: 'Custom web applications built with modern frameworks',
          link: '/services/web-development',
          icon: 'Globe'
        },
        {
          title: 'Mobile App Development',
          description: 'Native and cross-platform mobile applications',
          link: '/services/mobile-app-development',
          icon: 'Smartphone'
        },
        {
          title: 'AI Integration',
          description: 'AI-powered solutions and machine learning',
          link: '/services/ai-integration',
          icon: 'Cpu'
        },
        {
          title: 'Cloud & DevOps',
          description: 'Cloud infrastructure and CI/CD pipelines',
          link: '/services/cloud-devops',
          icon: 'Cloud'
        },
        {
          title: 'UI/UX Design',
          description: 'User-centered design and prototyping',
          link: '/services/ui-ux-design',
          icon: 'LayoutTemplate'
        },
        {
          title: 'Backend Development',
          description: 'Robust backend systems and APIs',
          link: '/services/backend-development',
          icon: 'Server'
        }
      ]
    },
    { 
      label: 'AI/ML', 
      path: '/ai-ml-services', 
      icon: 'Brain',
      hasDropdown: true,
      dropdownItems: [
        {
          title: 'Machine Learning',
          description: 'Custom ML models for prediction and classification',
          link: '/services/ai-ml-services/machine-learning',
          icon: 'Brain'
        },
        {
          title: 'Natural Language Processing',
          description: 'Advanced NLP solutions for text analysis',
          link: '/services/ai-ml-services/natural-language-processing',
          icon: 'FileText'
        },
        {
          title: 'Computer Vision',
          description: 'Image and video analysis solutions',
          link: '/services/ai-ml-services/computer-vision',
          icon: 'Eye'
        },
        {
          title: 'Predictive Analytics',
          description: 'Data-driven forecasting and modeling',
          link: '/services/ai-ml-services/predictive-analytics',
          icon: 'TrendingUp'
        },
        {
          title: 'AI Chatbots',
          description: 'Intelligent conversational AI solutions',
          link: '/services/ai-ml-services/ai-chatbots',
          icon: 'MessageCircle'
        },
        {
          title: 'Deep Learning',
          description: 'Advanced neural network solutions',
          link: '/services/ai-ml-services/deep-learning',
          icon: 'Network'
        },
        {
          title: 'Data Science',
          description: 'Comprehensive data science and analytics',
          link: '/services/ai-ml-services/data-science',
          icon: 'BarChart'
        },
        {
          title: 'AI Consulting',
          description: 'Strategic AI consulting and strategy',
          link: '/services/ai-ml-services/ai-consulting',
          icon: 'Lightbulb'
        },
        {
          title: 'Reinforcement Learning',
          description: 'Intelligent RL agents for autonomous systems',
          link: '/services/ai-ml-services/reinforcement-learning',
          icon: 'Network'
        }
      ]
    },
    { 
      label: 'BLOG', 
      path: '/blog', 
      icon: 'BookOpen'
    },
    { 
      label: 'HIRE DEVELOPERS', 
      path: '/services#pricing', 
      icon: 'Users',
      hasDropdown: true,
      dropdownItems: [
        {
          title: 'Hourly Engagement',
          description: 'Flexible hourly rates for project-based work',
          link: '/services#pricing'
        },
        {
          title: 'Monthly Dedicated',
          description: 'Dedicated developers on monthly contracts',
          link: '/services#pricing'
        },
        {
          title: 'Fixed Cost Projects',
          description: 'Complete project delivery with fixed pricing',
          link: '/services#pricing'
        }
      ]
    },
    { 
      label: 'ABOUT US', 
      path: '/about', 
      icon: 'Users'
    },
    { 
      label: 'CONTACT US', 
      path: '/contact', 
      icon: 'Mail'
    }
  ];

  const isActiveRoute = (path) => {
    // If path has a hash, check both pathname and hash
    if (path.includes('#')) {
      const [pathWithoutHash, hash] = path.split('#');
      return location.pathname === pathWithoutHash && location.hash === `#${hash}`;
    }
    // If path doesn't have a hash, only check pathname (ignore hash in URL)
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setOpenMobileDropdown(null);
  };

  const toggleMobileDropdown = (itemLabel) => {
    setOpenMobileDropdown(openMobileDropdown === itemLabel ? null : itemLabel);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Calculate header height (top bar + main nav bar)
      const headerHeight = 100; // Approximate height of both header bars
      const elementPosition = element.offsetTop - headerHeight;
      
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
    setOpenMobileDropdown(null);
  };

  return (
    <header className="bg-surface border-b border-border sticky top-0 z-50">
      {/* Top Bar with Contact Info and Social Media */}
      <div className="bg-[#00d690] text-white py-2">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-0">
            {/* Contact Information */}
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 sm:gap-6 sm:space-x-0 text-xs sm:text-base w-full sm:w-auto">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Icon name="Phone" size={16} className="text-white sm:w-5 sm:h-5" />
                <a href="tel:+917574082582" className="hover:text-white/80 transition-colors font-medium whitespace-nowrap">
                  +91 7574082582
                </a>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Icon name="Mail" size={16} className="text-white sm:w-5 sm:h-5" />
                <a href="mailto:business@octalchip.com" className="hover:text-white/80 transition-colors font-medium break-all sm:break-normal">
                  business@octalchip.com
                </a>
              </div>
            </div>

            {/* Social Media Icons */}
            <div className="hidden md:flex items-center space-x-6">
              <a 
                href="https://www.facebook.com/octalchip" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-white/80 transition-colors"
              >
                <Icon name="Facebook" size={24} />
              </a>
              <a 
                href="https://www.instagram.com/octalchip/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-white/80 transition-colors"
              >
                <Icon name="Instagram" size={24} />
              </a>
              <a 
                href="https://www.linkedin.com/company/octalchip" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-white/80 transition-colors"
              >
                <Icon name="Linkedin" size={24} />
              </a>
              <a 
                href="https://x.com/octalchip" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-white/80 transition-colors"
              >
                <Icon name="Twitter" size={24} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="bg-[#f4faf8] relative">
        <div className="container mx-auto px-4 sm:px-6 relative">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center ml-0 sm:ml-4">
              <img 
                src="/octalchiplogo.svg" 
                alt="OctalChip Logo" 
                className="h-8 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navigationItems.map((item) => (
                <div key={item.path} className="relative group">
                  <button
                    onClick={() => {
                      if (item.path.startsWith('/#')) {
                        scrollToSection(item.path.substring(2));
                      } else {
                        window.location.href = item.path;
                      }
                    }}
                    className={`flex items-center space-x-1 text-base font-medium transition-colors ${
                      isActiveRoute(item.path) ? 'text-[#00d690]' : 'text-gray-800 hover:text-[#00d690]'
                    }`}
                  >
                    <span>{item.label}</span>
                    {item.hasDropdown && (
                      <Icon name="ChevronDown" size={14} className="ml-1" />
                    )}
                  </button>
                  
                  {/* Dropdown Menu */}
                  {item.hasDropdown && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
                         style={{ 
                           minWidth: item.label === 'SERVICES' ? '1400px' : item.label === 'PRODUCT' ? '700px' : item.label === 'AI/ML' ? '1400px' : '250px',
                           transform: 'translateX(-40%)'
                         }}>
                      <div className="py-4">
                        {item.label === 'AI/ML' ? (
                          // AI/ML dropdown with four-column layout (2 for services, 2 for CTA)
                          <div className="grid grid-cols-4 gap-0">
                            {/* First 2 columns: AI/ML Services */}
                            <div className="col-span-2 grid grid-cols-2 gap-0">
                              {item.dropdownItems.map((dropdownItem, index) => (
                                <div key={index} className="px-4 py-3 hover:bg-[#00d690]/5 transition-colors">
                                  <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 mt-1">
                                      <Icon name={dropdownItem.icon} size={20} className="text-[#00d690]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between">
                                        <h4 className="text-sm font-medium text-gray-900 mb-1">
                                          {dropdownItem.title}
                                        </h4>
                                        {dropdownItem.link.startsWith('http') ? (
                                          <a 
                                            href={dropdownItem.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-3 py-1 rounded border border-[#00d690] hover:bg-[#00d690] hover:text-white"
                                          >
                                            View →
                                          </a>
                                        ) : (
                                          <Link 
                                            to={dropdownItem.link}
                                            className="text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-3 py-1 rounded border border-[#00d690] hover:bg-[#00d690] hover:text-white"
                                          >
                                            View →
                                          </Link>
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-600 leading-relaxed">
                                        {dropdownItem.description}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Last 2 columns: CTA */}
                            <div className="col-span-2 bg-[#f4faf8] px-6 py-6 flex flex-col justify-center">
                              <h3 className="text-xl font-bold text-gray-900 mb-3">
                                Transform with AI & ML
                              </h3>
                              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                                Leverage cutting-edge AI and Machine Learning solutions to drive innovation and growth.
                              </p>
                              <a
                                href="https://cal.com/octalchip/30min"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-[#00d690] hover:bg-[#00d690]/90 text-white px-6 py-3 rounded-md font-medium text-sm transition-colors inline-flex items-center justify-center w-fit"
                              >
                                Get Quote
                                <Icon name="ChevronRight" size={16} className="ml-2" />
                              </a>
                            </div>
                          </div>
                        ) : item.label === 'SERVICES' ? (
                          // Services dropdown with four-column layout (2 for services, 2 for CTA)
                          <div className="grid grid-cols-4 gap-0">
                            {/* First 2 columns: Services */}
                            <div className="col-span-2 grid grid-cols-2 gap-0">
                              {item.dropdownItems.map((dropdownItem, index) => (
                                <div key={index} className="px-4 py-3 hover:bg-[#00d690]/5 transition-colors">
                                  <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 mt-1">
                                      <Icon name={dropdownItem.icon} size={20} className="text-[#00d690]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between">
                                        <h4 className="text-sm font-medium text-gray-900 mb-1">
                                          {dropdownItem.title}
                                        </h4>
                                        {dropdownItem.link.startsWith('http') ? (
                                          <a 
                                            href={dropdownItem.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-3 py-1 rounded border border-[#00d690] hover:bg-[#00d690] hover:text-white"
                                          >
                                            View →
                                          </a>
                                        ) : (
                                          <Link 
                                            to={dropdownItem.link}
                                            className="text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-3 py-1 rounded border border-[#00d690] hover:bg-[#00d690] hover:text-white"
                                          >
                                            View →
                                          </Link>
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-600 leading-relaxed">
                                        {dropdownItem.description}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Last 2 columns: CTA */}
                            <div className="col-span-2 bg-[#f4faf8] px-6 py-6 flex flex-col justify-center">
                              <h3 className="text-xl font-bold text-gray-900 mb-3">
                                Power Up with Our Services
                              </h3>
                              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                                Bring intelligent solutions to your platform with our expert development services.
                              </p>
                              <a
                                href="https://cal.com/octalchip/30min"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-[#00d690] hover:bg-[#00d690]/90 text-white px-6 py-3 rounded-md font-medium text-sm transition-colors inline-flex items-center justify-center w-fit"
                              >
                                Get Quote
                                <Icon name="ChevronRight" size={16} className="ml-2" />
                              </a>
                            </div>
                          </div>
                        ) : item.label === 'PRODUCT' ? (
                          // Product dropdown with four-column layout (2 for products, 2 for CTA)
                          <div className="grid grid-cols-4 gap-0">
                            {/* First 2 columns: Products */}
                            <div className="col-span-2">
                              {item.dropdownItems.map((dropdownItem, index) => (
                                <div key={index} className="px-4 py-3 hover:bg-[#00d690]/5 transition-colors">
                                  <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 mt-1">
                                      <Icon name={dropdownItem.icon} size={20} className="text-[#00d690]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between">
                                        <h4 className="text-sm font-medium text-gray-900 mb-1">
                                          {dropdownItem.title}
                                        </h4>
                                        {dropdownItem.link.startsWith('http') ? (
                                          <a 
                                            href={dropdownItem.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-3 py-1 rounded border border-[#00d690] hover:bg-[#00d690] hover:text-white"
                                          >
                                            View →
                                          </a>
                                        ) : (
                                          <Link 
                                            to={dropdownItem.link}
                                            className="text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-3 py-1 rounded border border-[#00d690] hover:bg-[#00d690] hover:text-white"
                                          >
                                            View →
                                          </Link>
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-600 leading-relaxed">
                                        {dropdownItem.description}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Last 2 columns: CTA */}
                            <div className="col-span-2 bg-[#f4faf8] px-6 py-6 flex flex-col justify-center">
                              <h3 className="text-xl font-bold text-gray-900 mb-3">
                                Explore Our Products
                              </h3>
                              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                                Discover innovative solutions designed to streamline your business operations.
                              </p>
                              <a
                                href="https://cal.com/octalchip/30min"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-[#00d690] hover:bg-[#00d690]/90 text-white px-6 py-3 rounded-md font-medium text-sm transition-colors inline-flex items-center justify-center w-fit"
                              >
                                Get Quote
                                <Icon name="ChevronRight" size={16} className="ml-2" />
                              </a>
                            </div>
                          </div>
                        ) : (
                          // Other dropdowns (Hire Developers, etc.)
                          <>
                            {item.dropdownItems.map((dropdownItem, index) => (
                              <div key={index} className="px-4 py-3 hover:bg-[#00d690]/5 transition-colors">
                                {dropdownItem.link.startsWith('http') ? (
                                  <a 
                                    href={dropdownItem.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block text-sm text-gray-700 hover:text-[#00d690] transition-colors"
                                  >
                                    {dropdownItem.title}
                                  </a>
                                ) : (
                                  <Link 
                                    to={dropdownItem.link}
                                    className="block text-sm text-gray-700 hover:text-[#00d690] transition-colors"
                                  >
                                    {dropdownItem.title}
                                  </Link>
                                )}
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <a
                href="https://cal.com/octalchip/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#00d690] hover:bg-[#00d690]/90 text-white px-6 py-3 rounded-md font-medium text-sm transition-colors inline-block"
              >
                BOOK A 30 MIN CALL
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={toggleMobileMenu}
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={20} className="text-gray-800" />
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="lg:hidden border-t border-gray-200 bg-white">
              <nav className="py-4 space-y-1">
                {navigationItems.map((item) => (
                  <div key={item.path}>
                    <div className="flex items-center mx-2">
                      <button
                        onClick={() => {
                          if (item.path.startsWith('/#')) {
                            scrollToSection(item.path.substring(2));
                          } else if (item.path.includes('#')) {
                            // Handle paths with hash (e.g., /services#pricing)
                            const [path, hash] = item.path.split('#');
                            navigate(path);
                            setIsMobileMenuOpen(false);
                            // Scroll to section after navigation
                            setTimeout(() => {
                              scrollToSection(hash);
                            }, 100);
                          } else {
                            // Navigate to the page
                            if (item.path.startsWith('http')) {
                              window.open(item.path, '_blank');
                            } else {
                              navigate(item.path);
                            }
                            setIsMobileMenuOpen(false);
                          }
                        }}
                        className={`flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-md flex-1 text-left ${
                          isActiveRoute(item.path)
                            ? 'text-[#00d690] bg-[#00d690]/5' : 'text-gray-700 hover:text-[#00d690] hover:bg-[#00d690]/5'
                        }`}
                      >
                        <Icon name={item.icon} size={18} />
                        <span>{item.label}</span>
                      </button>
                      {item.hasDropdown && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleMobileDropdown(item.label);
                          }}
                          className="px-2 py-3 text-gray-700 hover:text-[#00d690] transition-colors"
                          aria-label="Toggle dropdown"
                        >
                          <Icon 
                            name={openMobileDropdown === item.label ? 'ChevronUp' : 'ChevronDown'} 
                            size={14} 
                          />
                        </button>
                      )}
                    </div>
                    
                    {/* Mobile Dropdown */}
                    {item.hasDropdown && openMobileDropdown === item.label && (
                      <div className="ml-8 mr-2 mt-1 bg-gray-50 rounded-md">
                        {item.label === 'SERVICES' || item.label === 'AI/ML' ? (
                          // Services and AI/ML dropdown with simple single-column layout for mobile
                          <div className="px-2 py-2">
                            {item.dropdownItems.map((dropdownItem, index) => (
                              <div key={index} className="px-3 py-2">
                                {dropdownItem.link.startsWith('http') ? (
                                  <a 
                                    href={dropdownItem.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block text-sm text-gray-700 hover:text-[#00d690] transition-colors"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    {dropdownItem.title}
                                  </a>
                                ) : (
                                  <Link 
                                    to={dropdownItem.link}
                                    className="block text-sm text-gray-700 hover:text-[#00d690] transition-colors"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    {dropdownItem.title}
                                  </Link>
                                )}
                              </div>
                            ))}
                            {item.label === 'SERVICES' && (
                              <div className="px-3 py-2 border-t border-gray-200 mt-2">
                                <Link 
                                  to="/services"
                                  className="block text-sm font-medium text-[#00d690] hover:text-[#00b876] transition-colors"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  View All Services →
                                </Link>
                              </div>
                            )}
                            {item.label === 'AI/ML' && (
                              <div className="px-3 py-2 border-t border-gray-200 mt-2">
                                <Link 
                                  to="/ai-ml-services"
                                  className="block text-sm font-medium text-[#00d690] hover:text-[#00b876] transition-colors"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  View All AI/ML Services →
                                </Link>
                              </div>
                            )}
                          </div>
                        ) : (
                          // Other dropdowns (Product, Hire Developers, etc.)
                          <>
                            {item.dropdownItems.map((dropdownItem, index) => (
                              <div key={index} className="px-4 py-3">
                                {(item.label === 'PRODUCT') ? (
                                  // Product dropdown with description and icon
                                  <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 mt-1">
                                      <Icon name={dropdownItem.icon} size={18} className="text-[#00d690]" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between mb-2">
                                        <h4 className="text-sm font-medium text-gray-900">
                                          {dropdownItem.title}
                                        </h4>
                                        {dropdownItem.link.startsWith('http') ? (
                                          <a 
                                            href={dropdownItem.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-3 py-1 rounded border border-[#00d690] hover:bg-[#00d690] hover:text-white"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                          >
                                            View →
                                          </a>
                                        ) : (
                                          <Link 
                                            to={dropdownItem.link}
                                            className="text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-3 py-1 rounded border border-[#00d690] hover:bg-[#00d690] hover:text-white"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                          >
                                            View →
                                          </Link>
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-600 leading-relaxed">
                                        {dropdownItem.description}
                                      </p>
                                    </div>
                                  </div>
                                ) : (
                                  // Simple dropdown for other items
                                  <a 
                                    href={dropdownItem.link}
                                    className="block text-sm text-gray-700 hover:text-[#00d690] transition-colors"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    {dropdownItem.title}
                                  </a>
                                )}
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Mobile CTA Button */}
                <div className="px-4 py-3">
                  <a
                    href="https://cal.com/octalchip/30min"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-[#00d690] hover:bg-[#00d690]/90 text-white px-6 py-2 rounded-md font-medium text-xs transition-colors inline-block text-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    BOOK A 30 MIN CALL
                  </a>
                </div>

                {/* Contact Info - Mobile */}
                <div className="px-4 py-3 border-t border-gray-200 space-y-2">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Icon name="Phone" size={16} className="text-[#00d690]" />
                    <a href="tel:+917574082582" className="hover:text-[#00d690] transition-colors">
                      +91 7574082582
                    </a>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Icon name="Mail" size={16} className="text-[#00d690]" />
                    <a href="mailto:business@octalchip.com" className="hover:text-[#00d690] transition-colors">
                      business@octalchip.com
                    </a>
                  </div>
                </div>
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;